import { Calendar, Clock, ExternalLink, Loader, Share2, User, Users, Video, Phone } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface PositionInterviewsViewProps {
  interviews: any[]
  loading?: boolean
  onRefresh?: () => void
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return { date: 'Not scheduled', time: '' }
  }

  const date = new Date(value)
  return {
    date: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }
}

const getStatusBadge = (status?: string, isDraft?: boolean) => {
  if (isDraft) return 'bg-orange-100 text-orange-800'
  switch ((status || '').toLowerCase()) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-800'
    case 'in-progress':
      return 'bg-yellow-100 text-yellow-800'
    case 'completed':
      return 'bg-green-100 text-green-800'
    case 'cancelled':
      return 'bg-red-100 text-red-800'
    case 'no-show':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getFormatIcon = (format?: string) => {
  switch ((format || '').toLowerCase()) {
    case 'video':
      return <Video className="w-4 h-4 text-blue-500" />
    case 'phone':
      return <Phone className="w-4 h-4 text-emerald-500" />
    default:
      return <Calendar className="w-4 h-4 text-gray-500" />
  }
}

const getShareBadge = (interview: any) => {
  if (interview.shareType === 'shared_team') {
    return {
      icon: <Users className="w-3 h-3 mr-1" />,
      text: interview.sharedTeamName ? `Shared with ${interview.sharedTeamName}` : 'Shared with your team'
    }
  }
  if (interview.shareType === 'shared_individual') {
    return {
      icon: <Share2 className="w-3 h-3 mr-1" />,
      text: interview.sharedByName ? `Shared by ${interview.sharedByName}` : 'Shared with you'
    }
  }
  return null
}

export default function PositionInterviewsView({ interviews, loading, onRefresh }: PositionInterviewsViewProps) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center text-gray-500">
          <Loader className="w-6 h-6 animate-spin mb-3" />
          <p>Loading interviews...</p>
        </div>
      </div>
    )
  }

  if (!loading && (!interviews || interviews.length === 0)) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 text-blue-600 mb-4">
          <Calendar className="w-7 h-7" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">No interviews yet</h3>
        <p className="text-gray-500 mt-2">Schedule an interview to see it appear here.</p>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Refresh list
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="divide-y divide-gray-200">
      {interviews.map((interview) => {
        const candidateFirst = interview.candidate?.first_name || ''
        const candidateLast = interview.candidate?.last_name || ''
        const candidateName = `${candidateFirst} ${candidateLast}`.trim() || 'Candidate'
        const candidateEmail = interview.candidate?.email || 'No email available'
        const scheduled = formatDateTime(interview.scheduled_at)
        const shareBadge = getShareBadge(interview)
        const statusLabel = interview.is_draft ? 'draft' : (interview.status ? interview.status.replace(/_/g, ' ') : 'unknown')

        return (
          <div key={interview.id} className="p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                  {candidateName.split(' ').map(part => part[0]).join('').slice(0, 2) || 'NA'}
                </div>
                <div>
                  <p className="text-base font-semibold text-gray-900">{candidateName}</p>
                  <p className="text-sm text-gray-500">{candidateEmail}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center">
                      {getFormatIcon(interview.format)}
                      <span className="ml-1 capitalize">{interview.format || 'format TBD'}</span>
                    </span>
                    <span className="inline-flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {interview.duration_minutes ? `${interview.duration_minutes} mins` : 'Flexible'}
                    </span>
                    <span className="inline-flex items-center capitalize">
                      <User className="w-3 h-3 mr-1" />
                      {interview.stage || 'Stage TBD'}
                    </span>
                    {shareBadge && (
                      <span className="inline-flex items-center text-blue-600">
                        {shareBadge.icon}
                        {shareBadge.text}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Scheduled Date</p>
                    <p className="font-medium">{scheduled.date}</p>
                    {scheduled.time && <p className="text-gray-500">{scheduled.time}</p>}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Interview Name</p>
                  <p className="font-medium text-gray-900">{interview.name || interview.title || 'Untitled interview'}</p>
                  <p className="text-gray-500 capitalize">{interview.interview_type || 'technical'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Position</p>
                  <p className="font-medium text-gray-900">{interview.position?.title || 'Unknown position'}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start lg:items-end gap-3">
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadge(interview.status, interview.is_draft)}`}>
                {statusLabel}
              </span>
              <button
                onClick={() => navigate(`/recruiter/interviews/${interview.id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View details
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
